
/* ==========================================================================
   EDITORIAL REVIEWS DRAWER ENGINE (/database/js/reviews-drawer.js)
   Review API v2 consumes SFF-normalized blocks rather than Cornerstone DOM.
   ========================================================================== */

import { escapeHtml, sanitizeUrl, sanitizeUrlValue } from '../utils.js?v=20260819-audit2';
import { fetchCaseReviews } from '../api.js?v=20260819-phase35';
import { GalleryModal } from './gallery-modal.js?v=20260819-phase35';
import { state } from '../state.js?v=20260819-audit2';
import { activateAccessibleLayer, deactivateAccessibleLayer } from '../a11y.js?v=20260819-audit2';

let previousBodyOverflow = '';
let lightboxState = { images: [], index: 0, title: '' };

function safeImages(images) {
  const seen = new Set();
  return (Array.isArray(images) ? images : []).filter(img => {
    const full = sanitizeUrlValue(img?.full_url || img?.thumb_url || '');
    if (!full || full === '#' || seen.has(full)) return false;
    seen.add(full);
    return true;
  });
}

function imageMarkup(image, className = 'review-v2-image') {
  const src = sanitizeUrl(image?.thumb_url || image?.full_url || '');
  const alt = escapeHtml(image?.alt || 'Review image');
  return `<img src="${src}" data-full-src="${sanitizeUrl(image?.full_url || image?.thumb_url || '')}" alt="${alt}" loading="lazy" class="${className}">`;
}

function renderTextBlock(block) {
  return `<div class="review-v2-copy">${block?.html || ''}</div>`;
}

function renderSpecGrid(block) {
  const items = Array.isArray(block?.items) ? block.items : [];
  if (!items.length) return '';
  return `
    <div class="review-v2-spec-grid">
      ${items.map(item => {
        const href = sanitizeUrl(item?.url || '');
        const link = item?.url && href !== '#'
          ? `<a href="${href}" target="_blank" rel="noopener noreferrer" class="review-v2-spec-link">${escapeHtml(item?.link_label || 'OPEN ↗')}</a>`
          : '';
        return `
          <div class="review-v2-spec-item">
            <span class="review-v2-spec-label">${escapeHtml(item?.label || '')}</span>
            <div class="review-v2-spec-value">${item?.value_html || ''}</div>
            ${link}
          </div>`;
      }).join('')}
    </div>`;
}

function renderImageBlock(block, blockId) {
  const image = block?.image;
  if (!image) return '';
  return `
    <button type="button" class="review-v2-single-image" data-review-open-block="${blockId}" aria-label="Open review image">
      ${imageMarkup(image)}
    </button>`;
}

function renderImageGrid(block, blockId) {
  const images = safeImages(block?.images);
  if (!images.length) return '';
  return `
    <div class="review-v2-image-grid review-v2-image-grid-${Math.min(images.length, 4)}">
      ${images.map((img, idx) => `
        <button type="button" class="review-v2-grid-image" data-review-open-block="${blockId}" data-review-image-index="${idx}" aria-label="Open image ${idx + 1} of ${images.length}">
          ${imageMarkup(img)}
        </button>`).join('')}
    </div>`;
}

function renderCarousel(block, blockId) {
  const images = safeImages(block?.images);
  if (!images.length) return '';
  const current = images[0];
  return `
    <div class="review-v2-carousel" data-review-carousel="${blockId}" data-review-index="0">
      <div class="review-v2-carousel-stage">
        <button type="button" class="review-v2-carousel-nav review-v2-carousel-prev" data-review-carousel-step="-1" aria-label="Previous review image">‹</button>
        <button type="button" class="review-v2-carousel-image-btn" data-review-open-block="${blockId}" aria-label="Open carousel image">
          ${imageMarkup(current, 'review-v2-carousel-image')}
        </button>
        <button type="button" class="review-v2-carousel-nav review-v2-carousel-next" data-review-carousel-step="1" aria-label="Next review image">›</button>
        <span class="review-v2-carousel-counter">1 / ${images.length}</span>
      </div>
      ${images.length > 1 ? `
        <div class="review-v2-carousel-thumbs" role="list" aria-label="Review image thumbnails">
          ${images.map((img, idx) => `
            <button type="button" class="review-v2-carousel-thumb ${idx === 0 ? 'active' : ''}" data-review-carousel-thumb="${idx}" aria-label="Show image ${idx + 1}" aria-pressed="${idx === 0 ? 'true' : 'false'}">
              ${imageMarkup(img, 'review-v2-carousel-thumb-img')}
            </button>`).join('')}
        </div>` : ''}
    </div>`;
}

function renderGallery(block, blockId) {
  const images = safeImages(block?.images);
  if (!images.length) {
    return `
      <button type="button" class="review-v2-gallery-fallback" data-review-case-gallery="1">
        <span>PHOTO GALLERY</span>
        <strong>VIEW CASE GALLERY ↗</strong>
      </button>`;
  }

  const preview = images.slice(0, 3);
  return `
    <button type="button" class="review-v2-gallery-card" data-review-open-block="${blockId}" aria-label="Open ${images.length} photo review gallery">
      <span class="review-v2-gallery-preview review-v2-gallery-preview-${preview.length}">
        ${preview.map(img => imageMarkup(img, 'review-v2-gallery-thumb')).join('')}
      </span>
      <span class="review-v2-gallery-meta">
        <span>${escapeHtml(block?.label || 'Photo Gallery')}</span>
        <strong>VIEW ${images.length} PHOTO${images.length === 1 ? '' : 'S'} ↗</strong>
      </span>
    </button>`;
}

function renderVideo(block) {
  const src = sanitizeUrl(block?.src || '');
  if (!src || src === '#') return '';
  const title = escapeHtml(block?.title || 'Embedded review video');
  return `
    <div class="review-v2-video">
      <iframe
        loading="lazy"
        src="${src}"
        title="${title}"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerpolicy="strict-origin-when-cross-origin"
        allowfullscreen></iframe>
    </div>`;
}

function renderBlock(block, blockId, registry) {
  if (!block || typeof block !== 'object') return '';
  registry.set(blockId, block);

  switch (block.type) {
    case 'text': return renderTextBlock(block);
    case 'spec_grid': return renderSpecGrid(block);
    case 'image': return renderImageBlock(block, blockId);
    case 'image_grid': return renderImageGrid(block, blockId);
    case 'carousel': return renderCarousel(block, blockId);
    case 'gallery': return renderGallery(block, blockId);
    case 'video': return renderVideo(block);
    default: return '';
  }
}

function renderSection(section, reviewId, sectionIndex, registry) {
  const blocks = Array.isArray(section?.blocks) ? section.blocks : null;
  const verdict = Boolean(section?.is_verdict) || section?.kind === 'verdict';
  const kind = escapeHtml(section?.kind || 'editorial');

  // Compatibility path for a PHP/JS staggered deployment.
  if (!blocks) {
    return `
      <section class="review-v2-section ${verdict ? 'review-v2-verdict' : ''}" data-review-kind="legacy">
        ${section?.heading ? `<h4 class="review-v2-section-title">${escapeHtml(section.heading)}</h4>` : ''}
        <div class="review-v2-legacy-html">${section?.html || ''}</div>
      </section>`;
  }

  return `
    <section class="review-v2-section ${verdict ? 'review-v2-verdict' : ''}" data-review-kind="${kind}">
      ${section?.heading ? `<h4 class="review-v2-section-title">${escapeHtml(section.heading)}</h4>` : ''}
      <div class="review-v2-blocks">
        ${blocks.map((block, blockIndex) => renderBlock(block, `r${reviewId}-s${sectionIndex}-b${blockIndex}`, registry)).join('')}
      </div>
    </section>`;
}

function ensureReviewLightbox() {
  let modal = document.getElementById('sffReviewLightbox');
  if (modal) return modal;

  modal = document.createElement('div');
  modal.id = 'sffReviewLightbox';
  modal.className = 'review-v2-lightbox';
  modal.style.display = 'none';
  modal.setAttribute('aria-hidden', 'true');
  modal.innerHTML = `
    <div class="review-v2-lightbox-shell">
      <div class="review-v2-lightbox-header">
        <span class="review-v2-lightbox-title">REVIEW GALLERY</span>
        <span class="review-v2-lightbox-counter"></span>
        <button type="button" class="review-v2-lightbox-close" aria-label="Close review gallery">×</button>
      </div>
      <div class="review-v2-lightbox-stage">
        <button type="button" class="review-v2-lightbox-nav review-v2-lightbox-prev" aria-label="Previous image">‹</button>
        <img class="review-v2-lightbox-image" alt="Review gallery image">
        <button type="button" class="review-v2-lightbox-nav review-v2-lightbox-next" aria-label="Next image">›</button>
      </div>
      <div class="review-v2-lightbox-thumbs" aria-label="Review gallery thumbnails"></div>
    </div>`;

  document.body.appendChild(modal);
  modal.querySelector('.review-v2-lightbox-close')?.addEventListener('click', closeReviewLightbox);
  modal.querySelector('.review-v2-lightbox-prev')?.addEventListener('click', () => stepReviewLightbox(-1));
  modal.querySelector('.review-v2-lightbox-next')?.addEventListener('click', () => stepReviewLightbox(1));
  modal.addEventListener('click', e => {
    if (e.target === modal) closeReviewLightbox();
  });
  modal.addEventListener('keydown', e => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); stepReviewLightbox(-1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); stepReviewLightbox(1); }
  });

  return modal;
}

function syncReviewLightbox() {
  const modal = document.getElementById('sffReviewLightbox');
  if (!modal || !lightboxState.images.length) return;

  const total = lightboxState.images.length;
  lightboxState.index = ((lightboxState.index % total) + total) % total;
  const current = lightboxState.images[lightboxState.index];

  const img = modal.querySelector('.review-v2-lightbox-image');
  if (img) {
    img.src = sanitizeUrlValue(current?.full_url || current?.thumb_url || '');
    img.alt = current?.alt || `Review image ${lightboxState.index + 1}`;
  }
  const counter = modal.querySelector('.review-v2-lightbox-counter');
  if (counter) counter.textContent = `${lightboxState.index + 1} / ${total}`;
  const title = modal.querySelector('.review-v2-lightbox-title');
  if (title) title.textContent = lightboxState.title || 'REVIEW GALLERY';

  const thumbs = modal.querySelector('.review-v2-lightbox-thumbs');
  if (thumbs) {
    thumbs.innerHTML = lightboxState.images.map((item, idx) => `
      <button type="button" class="review-v2-lightbox-thumb ${idx === lightboxState.index ? 'active' : ''}" data-review-lightbox-index="${idx}" aria-label="Show image ${idx + 1}" aria-pressed="${idx === lightboxState.index ? 'true' : 'false'}">
        ${imageMarkup(item, 'review-v2-lightbox-thumb-img')}
      </button>`).join('');
    thumbs.querySelectorAll('[data-review-lightbox-index]').forEach(btn => {
      btn.addEventListener('click', () => {
        lightboxState.index = parseInt(btn.getAttribute('data-review-lightbox-index'), 10) || 0;
        syncReviewLightbox();
      });
    });
  }

  modal.querySelectorAll('.review-v2-lightbox-nav').forEach(btn => {
    btn.style.display = total > 1 ? '' : 'none';
  });
}

function openReviewLightbox(images, startIndex = 0, title = 'REVIEW GALLERY') {
  const safe = safeImages(images);
  if (!safe.length) return;

  const modal = ensureReviewLightbox();
  lightboxState = { images: safe, index: Math.max(0, Math.min(startIndex, safe.length - 1)), title };
  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  modal.style.display = 'flex';
  modal.classList.add('active');
  syncReviewLightbox();
  activateAccessibleLayer(modal, {
    label: title,
    initialFocusSelector: '.review-v2-lightbox-close',
    onClose: closeReviewLightbox
  });
}

function closeReviewLightbox() {
  const modal = document.getElementById('sffReviewLightbox');
  if (!modal) return;
  deactivateAccessibleLayer(modal);
  modal.classList.remove('active');
  modal.style.display = 'none';
  document.body.style.overflow = previousBodyOverflow;
}

function stepReviewLightbox(delta) {
  if (!lightboxState.images.length) return;
  lightboxState.index += delta;
  syncReviewLightbox();
}

function syncInlineCarousel(carousel, block) {
  const images = safeImages(block?.images);
  if (!images.length) return;
  const total = images.length;
  let index = parseInt(carousel.getAttribute('data-review-index'), 10) || 0;
  index = ((index % total) + total) % total;
  carousel.setAttribute('data-review-index', String(index));

  const hero = carousel.querySelector('.review-v2-carousel-image');
  if (hero) {
    hero.src = sanitizeUrlValue(images[index]?.thumb_url || images[index]?.full_url || '');
    hero.setAttribute('data-full-src', sanitizeUrlValue(images[index]?.full_url || images[index]?.thumb_url || ''));
    hero.alt = images[index]?.alt || `Review image ${index + 1}`;
  }
  const counter = carousel.querySelector('.review-v2-carousel-counter');
  if (counter) counter.textContent = `${index + 1} / ${total}`;

  carousel.querySelectorAll('[data-review-carousel-thumb]').forEach(btn => {
    const thumbIndex = parseInt(btn.getAttribute('data-review-carousel-thumb'), 10) || 0;
    const active = thumbIndex === index;
    btn.classList.toggle('active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  });
}

function bindReviewInteractions(drawer, registry, caseIdx) {
  drawer.addEventListener('click', e => {
    const stepBtn = e.target.closest('[data-review-carousel-step]');
    if (stepBtn && drawer.contains(stepBtn)) {
      const carousel = stepBtn.closest('[data-review-carousel]');
      const id = carousel?.getAttribute('data-review-carousel');
      const block = id ? registry.get(id) : null;
      if (carousel && block) {
        const delta = parseInt(stepBtn.getAttribute('data-review-carousel-step'), 10) || 0;
        const current = parseInt(carousel.getAttribute('data-review-index'), 10) || 0;
        carousel.setAttribute('data-review-index', String(current + delta));
        syncInlineCarousel(carousel, block);
      }
      return;
    }

    const thumbBtn = e.target.closest('[data-review-carousel-thumb]');
    if (thumbBtn && drawer.contains(thumbBtn)) {
      const carousel = thumbBtn.closest('[data-review-carousel]');
      const id = carousel?.getAttribute('data-review-carousel');
      const block = id ? registry.get(id) : null;
      if (carousel && block) {
        carousel.setAttribute('data-review-index', thumbBtn.getAttribute('data-review-carousel-thumb') || '0');
        syncInlineCarousel(carousel, block);
      }
      return;
    }

    const openBtn = e.target.closest('[data-review-open-block]');
    if (openBtn && drawer.contains(openBtn)) {
      const id = openBtn.getAttribute('data-review-open-block');
      const block = registry.get(id);
      if (!block) return;

      let images = [];
      if (block.type === 'image' && block.image) images = [block.image];
      else images = safeImages(block.images);
      if (!images.length) return;

      let start = parseInt(openBtn.getAttribute('data-review-image-index'), 10);
      if (!Number.isFinite(start)) {
        const carousel = openBtn.closest('[data-review-carousel]');
        start = carousel ? (parseInt(carousel.getAttribute('data-review-index'), 10) || 0) : 0;
      }
      openReviewLightbox(images, start, block.label || 'REVIEW GALLERY');
      return;
    }

    const fallback = e.target.closest('[data-review-case-gallery]');
    if (fallback && drawer.contains(fallback)) {
      const caseObj = state.fetchedCasesGlobal[caseIdx];
      if (caseObj) GalleryModal.open(caseObj);
    }
  });
}

export async function toggleReviewsDrawer(reviewsBtn) {
  const caseSlug = reviewsBtn.getAttribute('data-slug');
  const caseIdx = parseInt(reviewsBtn.getAttribute('data-idx'), 10);
  const drawerId = reviewsBtn.getAttribute('data-target');
  const drawer = document.getElementById(drawerId);
  if (!drawer) return;
  reviewsBtn.setAttribute('aria-controls', drawerId);

  const isOpen = drawer.style.display === 'block';
  if (isOpen) {
    drawer.style.display = 'none';
    drawer.setAttribute('aria-hidden', 'true');
    reviewsBtn.classList.remove('active');
    reviewsBtn.setAttribute('aria-expanded', 'false');
    return;
  }

  drawer.style.display = 'block';
  drawer.setAttribute('aria-hidden', 'false');
  reviewsBtn.classList.add('active');
  reviewsBtn.setAttribute('aria-expanded', 'true');

  if (drawer.innerHTML.trim() !== '') return;

  drawer.innerHTML = `<div class="review-v2-status">FETCHING REVIEWS...</div>`;
  const data = await fetchCaseReviews(caseSlug);

  if (!data || !Array.isArray(data.reviews) || data.reviews.length === 0) {
    drawer.innerHTML = `<div class="review-v2-status">NO REVIEWS PUBLISHED FOR THIS CASE.</div>`;
    return;
  }

  const registry = new Map();
  let html = `
    <div class="review-summary-bar font-mono">
      <span class="review-summary-title">REVIEWS (${data.count})</span>
      <span class="review-summary-source">EDITORIAL ARCHIVE</span>
    </div>
    <div class="review-articles-container font-mono">`;

  data.reviews.forEach(rev => {
    html += `
      <article class="review-post-card">
        <header class="review-post-header">
          <div class="review-post-heading-wrap">
            <h3 class="review-post-title">${escapeHtml(rev.title)}</h3>
            <span class="review-post-byline">By ${escapeHtml(rev.author)} · Published ${escapeHtml(rev.date)}</span>
          </div>
          <a href="${sanitizeUrl(rev.permalink)}" target="_blank" rel="noopener noreferrer" class="sff-action-pill review-full-article-link" title="Read full review article">READ FULL ARTICLE ↗</a>
        </header>
        <div class="review-sections-grid">
          ${(Array.isArray(rev.sections) ? rev.sections : []).map((section, sectionIndex) => renderSection(section, rev.review_id, sectionIndex, registry)).join('')}
        </div>
      </article>`;
  });

  html += `</div>`;
  drawer.innerHTML = html;
  bindReviewInteractions(drawer, registry, caseIdx);
}
